import { createSlice } from '@reduxjs/toolkit';
import { AuthState } from 'src/types/auth';

// Set the initial state values of the state object that this slice will manage
export const INITIAL_STATE: AuthState = {
  accessToken: '',
  authCode: '',
  cognitoEmailAddress: '',
  cognitoUsername: '',
  createAccountEmailAddress: '',
  idToken: '',
  isAccountCreationCompleted: false,
  isAuthCodeRequired: false,
  isUserLoginCompleted: false,
  isWorkFailure: false,
  isWorkStarted: false,
  isWorkSuccess: false,
  sendAuthCodeInProgress: false,
  userDetails: undefined,
  workErrorMsg: '',
  workProgressMsg: '',
};

// Define the slice and generate actions, reducers with createSlice
export const authSlice = createSlice({
  name: 'auth',
  initialState: INITIAL_STATE,
  reducers: {
    logout: (state) => ({ ...state, ...INITIAL_STATE, loginErrorMsg: '' }),
    workStarted: (state) => {
      state.isWorkStarted = true;
      state.isWorkSuccess = false;
      state.isWorkFailure = false;
      state.workErrorMsg = '';
    },
    workSucceeded: (state) => {
      state.isWorkStarted = false;
      state.isWorkSuccess = true;
      state.isWorkFailure = false;
    },
    workFailed: (state) => {
      state.isWorkStarted = false;
      state.isWorkSuccess = false;
      state.isWorkFailure = true;
    },
    resetWorkProgressState: (state) => {
      state.isWorkStarted = false;
      state.isWorkSuccess = false;
      state.isWorkFailure = false;
    },
    setProgressMessage: (state, action) => {
      const { message } = action.payload;
      state.workProgressMsg = message;
    },
    setErrorMessage: (state, action) => {
      const { message } = action.payload;
      state.workErrorMsg = message;
    },
    setCreateAccountEmailAddress: (state, action) => {
      state.createAccountEmailAddress = action.payload;
    },
    setAuthCodeRequired: (state, action) => {
      state.isAuthCodeRequired = action.payload;
    },
    setSendAuthCodeInProgress: (state, action) => {
      state.sendAuthCodeInProgress = action.payload;
    },
    setSubmitAuthCodeInProgress: (state, action) => {
      state.submitAuthCodeInProgress = action.payload;
    },
    setUserDetails: (state, action) => {
      state.userDetails = action.payload;
    },
    setCognitoEmailAddress: (state, action) => {
      state.cognitoEmailAddress = action.payload;
    },
    setCognitoUsername: (state, action) => {
      const { cognitoUsername } = action.payload;
      state.cognitoUsername = cognitoUsername;
    },
    setAccessToken: (state, action) => {
      state.accessToken = action.payload;
    },
    setIdToken: (state, action) => {
      state.idToken = action.payload;
    },
    setIsUserLoginCompleted: (state, action) => {
      state.isUserLoginCompleted = action.payload;
    },
    setIsAccountCreationCompleted: (state, action) => {
      state.isAccountCreationCompleted = action.payload;
    },
    cancelPendingAuth: (state) => {
      state.authCode = '';
      state.cognitoEmailAddress = '';
      state.cognitoUsername = '';
      state.createAccountEmailAddress = '';
      state.isAuthCodeRequired = false;
      state.isWorkFailure = false;
      state.isWorkStarted = false;
      state.isWorkSuccess = false;
      state.sendAuthCodeInProgress = false;
      state.workErrorMsg = '';
      state.workProgressMsg = '';
    },
    resetAuthState: () => INITIAL_STATE,
  },
});

// Export actions that were generated by createSlice
export const {
  cancelPendingAuth,
  logout,
  resetAuthState,
  resetWorkProgressState,
  setAccessToken,
  setAuthCodeRequired,
  setCognitoEmailAddress,
  setCognitoUsername,
  setCreateAccountEmailAddress,
  setErrorMessage,
  setIdToken,
  setIsAccountCreationCompleted,
  setIsUserLoginCompleted,
  setProgressMessage,
  setSendAuthCodeInProgress,
  setSubmitAuthCodeInProgress,
  setUserDetails,
  workFailed,
  workStarted,
  workSucceeded,
} = authSlice.actions;

// Export combined reducer that was generated by createSlice
// (this is a single reducer which is a combination of all reducers in the slice... it basically puts them in a switch statement)
export default authSlice.reducer;
